import { CustomFieldType } from 'src/api/generated';

/**
 * Maps CustomFieldType enum values to their database prefixes.
 * These prefixes are used to construct the actual field keys stored in the database.
 *
 * @example
 * // For a STRING field with key "color", the database key becomes "string_color"
 * const dbKey = CUSTOM_FIELD_TYPE_PREFIX[CustomFieldType.STRING] + "color";
 */
export const CUSTOM_FIELD_TYPE_PREFIX = {
  [CustomFieldType.STRING]: 'string_',
  [CustomFieldType.NUMBER]: 'int_',
  [CustomFieldType.DATE]: 'date_',
  [CustomFieldType.BOOLEAN]: 'bool_',
  [CustomFieldType.LIST]: 'string_list_',
  [CustomFieldType.FILE]: 'file_',
} as const;

/**
 * Constructs the database field key by prepending the type-specific prefix to the field key.
 *
 * @param fieldType - The type of the custom field
 * @param fieldKey - The original field key (without prefix)
 * @returns The prefixed database field key
 * @throws Error if the fieldType is not supported
 *
 * @example
 * getCustomFieldKey(CustomFieldType.NUMBER, "quantity") // => "int_quantity"
 */
export function getCustomFieldKey(
  fieldType: CustomFieldType,
  fieldKey: string
): string {
  const prefix = CUSTOM_FIELD_TYPE_PREFIX[fieldType];
  if (!prefix) {
    throw new Error(`Unsupported field type: ${fieldType}`);
  }

  return `${prefix}${fieldKey}`;
}

/**
 * Parses a prefixed database field key to extract the original field key and determine its type.
 *
 * @param key - The prefixed database field key
 * @returns An object containing the detected CustomFieldType and the original unprefixed key
 *
 * @example
 * parseCustomFieldKey("int_quantity") // => { type: CustomFieldType.NUMBER, originalKey: "quantity" }
 * parseCustomFieldKey("unknown_key") // => { type: CustomFieldType.STRING, originalKey: "unknown_key" }
 */
export function parseCustomFieldKey(key: string): {
  type: CustomFieldType;
  originalKey: string;
} {
  const matchingEntry = Object.entries(CUSTOM_FIELD_TYPE_PREFIX).find(
    ([_, prefix]) => key.startsWith(prefix)
  );

  if (!matchingEntry) {
    return {
      type: CustomFieldType.STRING,
      originalKey: key,
    };
  }

  const [typeKey, prefix] = matchingEntry;
  return {
    type: typeKey as unknown as CustomFieldType,
    originalKey: key.substring(prefix.length),
  };
}

/**
 * Parses a custom field value to its appropriate type.
 * Handles type conversion and validation based on the field type.
 *
 * @param value - The raw value to parse
 * @param fieldType - The expected field type
 * @returns The parsed value in the correct type, or null if invalid
 *
 * @example
 * parseCustomFieldValue("123", CustomFieldType.NUMBER) // => 123
 * parseCustomFieldValue("true", CustomFieldType.BOOLEAN) // => true
 */
export function parseCustomFieldValue(
  value: unknown,
  fieldType: CustomFieldType
): unknown {
  if (value === null || value === undefined) {
    return null;
  }

  switch (fieldType) {
    case CustomFieldType.NUMBER:
      return isNaN(Number(value)) ? null : Number(value);
    case CustomFieldType.BOOLEAN:
      return String(value).toLowerCase() === 'true';
    case CustomFieldType.DATE:
      return String(value);
    case CustomFieldType.LIST:
      return String(value);
    case CustomFieldType.FILE:
      if (typeof value === 'object') {
        return value;
      }
      try {
        return JSON.parse(String(value));
      } catch {
        return String(value);
      }
    case CustomFieldType.STRING:
    default:
      return String(value);
  }
}

/**
 * Formats a custom field value for display or storage as a string.
 * Handles null values and special formatting for different field types.
 *
 * @param value - The value to format
 * @param fieldType - The field type
 * @returns A string representation of the value
 *
 * @example
 * formatCustomFieldValue(true, CustomFieldType.BOOLEAN) // => "true"
 * formatCustomFieldValue({ name: "file.pdf" }, CustomFieldType.FILE) // => '{"name":"file.pdf"}'
 */
export function formatCustomFieldValue(
  value: unknown,
  fieldType: CustomFieldType
): string {
  if (value === null || value === undefined) {
    return '';
  }

  switch (fieldType) {
    case CustomFieldType.BOOLEAN:
      return Boolean(value).toString();
    case CustomFieldType.LIST:
      if (!value || value === 'null') return '';
      return String(value);
    case CustomFieldType.FILE:
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      return String(value);
    case CustomFieldType.DATE:
    case CustomFieldType.NUMBER:
    case CustomFieldType.STRING:
    default:
      return String(value);
  }
}

/**
 * Formats a database field name (with prefix) into a human-readable label.
 * Removes the type prefix and converts snake_case to Title Case.
 *
 * @param name - The prefixed field name from the database
 * @returns A formatted human-readable label
 *
 * @example
 * formatCustomFieldLabel("string_first_name") // => "First Name"
 * formatCustomFieldLabel("int_item_count") // => "Item Count"
 */
export function formatCustomFieldLabel(name: string): string {
  const prefixes = Object.values(CUSTOM_FIELD_TYPE_PREFIX);

  let result = name;
  for (const prefix of prefixes) {
    if (name.startsWith(prefix)) {
      result = name.substring(prefix.length);
      break;
    }
  }

  return result
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
